import { Box, Button, TextField, Typography, Alert } from "@mui/material";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import themeColors from "../theme";
import Logo from "../components/Logo";

export default function ResetPassword() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { teal, darkTeal, lightGray } = themeColors;

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const token = searchParams.get("token");

    useEffect(() => {
        if (!token) {
            setError("Invalid reset link. Please request a new password reset.");
        }
    }, [token]);

    const validatePassword = (value) => {
        const lengthOk = value.length >= 8;
        const hasNumber = /\d/.test(value);
        const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(value);

        if (!lengthOk || !hasNumber || !hasSpecial) {
            return "Password must be at least 8 characters, include a number and a special character.";
        }
        return "";
    };

    const handlePasswordChange = (e) => {
        const value = e.target.value;
        setPassword(value);
        setPasswordError(validatePassword(value));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (!token) {
            setError("Invalid reset link. Please request a new password reset.");
            return;
        }

        const validationMsg = validatePassword(password);
        if (validationMsg) {
            setPasswordError(validationMsg);
            return;
        }

        if (password !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }

        setLoading(true);
        try {
            await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/reset-password`, {
                token,
                password
            });
            setSuccess(true);
            setTimeout(() => {
                navigate("/login");
            }, 3000);
        } catch (err) {
            setError(err.response?.data?.error || "Failed to reset password. The link may have expired.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: lightGray,
                px: 2,
            }}
        >
            <Box sx={{ textAlign: "center", mb: 4 }}>
                <Logo/>
            </Box>

            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    width: "100%",
                    maxWidth: 400,
                    backgroundColor: "#fff",
                    p: 4,
                    borderRadius: 2,
                    boxShadow: 3,
                    textAlign: "center",
                }}
            >
                <Typography variant="h5" mb={3} fontWeight="bold">
                    Reset Password
                </Typography>

                {success ? (
                    <Alert severity="success" sx={{ mt: 2 }}>
                        Password has been reset successfully! Redirecting to login...
                    </Alert>
                ) : (
                    <>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="New Password"
                            type="password"
                            value={password}
                            onChange={handlePasswordChange}
                            error={!!passwordError}
                            helperText={passwordError}
                            disabled={loading || !token}
                        />

                        <TextField
                            fullWidth
                            margin="normal"
                            label="Confirm Password"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            disabled={loading || !token}
                        />

                        {error && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {error}
                            </Alert>
                        )}

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{
                                mt: 3,
                                backgroundColor: teal,
                                '&:hover': { backgroundColor: darkTeal }
                            }}
                            disabled={!!passwordError || loading || !token}
                        >
                            {loading ? "Resetting..." : "Reset Password"}
                        </Button>

                        <Button
                            variant="text"
                            onClick={() => navigate("/login")}
                            sx={{ mt: 2, color: "#1D3557", fontWeight: 500 }}
                            fullWidth
                        >
                            Back to Login
                        </Button>
                    </>
                )}
            </Box>
        </Box>
    );
}


